import { memo, useEffect, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import type { i18n } from 'i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col } from 'styled-bootstrap-grid';
import { z } from 'zod';

import type { TranslationForm } from '@/services/ws-translate/src/main/resources/rest-api';
import { getTranslations, updateTranslation } from '@/services/ws-translate/src/main/resources/rest-api';
import { TextField } from '@/js/libs/form';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useSubscribe } from '@/js/libs/observable';
import { Button, FormGroup, InnerGroup, Input, Label, Loader, Modal, useNotification } from '@/js/libs/ui/src/V2';
import { NotActive, useLazyRequest, useRequest } from '@/js/libs/use-request';

import { supportedLanguages } from '../../constants.tsx';
import { LanguageKey } from '../../types.ts';
import { useTranslateModeContext } from './context.ts';
import { EmptyState } from './styled.ts';

type UpdateLocaleCacheParams = {
  i18n: i18n;
  namespace: string;
  key: string;
  values: FormValues;
};

const languageFieldMap: Record<LanguageKey, keyof FormValues> = {
  [LanguageKey.English]: 'valueEn',
  [LanguageKey.German]: 'valueDe',
  [LanguageKey.French]: 'valueFr',
  [LanguageKey.Italian]: 'valueIt',
};

/**
 * Patches the LocalStorage cache and i18next resource bundle after a translation is saved,
 * so the updated value is visible immediately without a full page reload.
 *
 * For each supported language, updates the corresponding `i18next_res_{lng}-{ns}` JSON entry
 * in LocalStorage and resets `i18nStamp` to `now` so the cache TTL restarts — otherwise
 * the backend would overwrite the new value with the stale cached response on next load.
 * Also injects the new value into the in-memory i18next store via `addResource`.
 */
const updateLocaleCache = ({ i18n, namespace, key, values }: UpdateLocaleCacheParams) => {
  const now = Date.now();

  supportedLanguages.forEach((lng) => {
    const cacheKey = `i18next_res_${lng}-${namespace}`;
    const fieldKey = languageFieldMap[lng];
    const newValue = fieldKey ? values[fieldKey] : undefined;

    if (newValue === undefined) {
      return;
    }

    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached) as { i18nStamp?: number; [key: string]: unknown };
        parsed[key] = newValue;
        parsed.i18nStamp = now;
        localStorage.setItem(cacheKey, JSON.stringify(parsed));
      } catch {
        // If parsing fails, skip cache update for this language
      }
    }

    i18n.addResource(lng, namespace, key, newValue);
  });
};

type FormValues = {
  valueEn: string;
  valueDe: string;
  valueFr: string;
  valueIt: string;
};

const useTranslationEditorFormSchema = () => {
  const [t] = useTranslation(['messages']);

  return z.object({
    valueEn: z.string().min(1, t('messages:FIELD_IS_REQUIRED')),
    valueDe: z.string().min(1, t('messages:FIELD_IS_REQUIRED')),
    valueFr: z.string().min(1, t('messages:FIELD_IS_REQUIRED')),
    valueIt: z.string().min(1, t('messages:FIELD_IS_REQUIRED')),
  });
};

export const TranslationEditor = memo(function TranslationEditor() {
  const [t, i18n] = useTranslation(['common', 'messages', 'admin']);
  const { success } = useNotification();
  const schema = useTranslationEditorFormSchema();

  const editorModalState = useTranslateModeContext('editorModalState');
  const openOverlay = useTranslateModeContext('openOverlay');

  const { payload, isMounted } = editorModalState;

  const form = useForm<FormValues>({
    defaultValues: {
      valueEn: '',
      valueDe: '',
      valueFr: '',
      valueIt: '',
    },
    resolver: zodResolver(schema),
    mode: 'onChange',
  });

  const {
    formState: { isSubmitting },
    handleSubmit,
    control,
    reset,
  } = form;

  const fetchRequest = useRequest({
    apiMethod: getTranslations,
    params: payload
      ? {
          namespace: payload?.namespace,
          keyFilter: payload?.key,
          page: 0,
          limit: 100,
        }
      : NotActive,
  });
  const isFetching = useSubscribe(fetchRequest.pending$);
  const translationsResponse = useSubscribe(fetchRequest.data$, (response) => response?.data.items);
  const translation = useMemo(
    () => (payload && !isFetching ? translationsResponse?.find((item) => item.key === payload?.key) : undefined),
    [payload, isFetching, translationsResponse],
  );

  const isDisabled = isFetching || !translation?.id;

  const handleClose = useCallbackRef(() => {
    editorModalState.close();
    openOverlay();
  });

  const updateRequest = useLazyRequest({
    apiMethod: updateTranslation,
  });

  const handleSubmitForm = useCallbackRef(async (values: FormValues) => {
    if (!translation?.id || !payload?.namespace || !payload?.key) {
      return;
    }

    const formData: TranslationForm = {
      ...translation,
      ...values,
    };

    await updateRequest.load({ id: translation.id, form: formData });

    success({
      title: t('common:SUCCESS'),
      body: t('messages:TRANSLATION_WAS_UPDATED'),
    });

    updateLocaleCache({
      i18n,
      namespace: payload.namespace,
      key: payload.key,
      values,
    });

    i18n.changeLanguage(i18n.language);

    handleClose();
  });

  useEffect(() => {
    reset({
      valueEn: translation?.valueEn ?? '',
      valueDe: translation?.valueDe ?? '',
      valueFr: translation?.valueFr ?? '',
      valueIt: translation?.valueIt ?? '',
    });
  }, [translation, reset]);

  if (!isMounted) return null;

  return (
    <Modal state={editorModalState} title={t('admin:EDIT_TRANSLATION')}>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(handleSubmitForm)}>
          <Modal.Body>
            {isFetching && <Loader cover />}
            <FormGroup>
              <Col md={6}>
                <InnerGroup>
                  <Label>{t('common:NAMESPACE')}</Label>
                  <Input value={payload?.namespace} disabled />
                </InnerGroup>
              </Col>
              <Col md={6}>
                <InnerGroup>
                  <Label>{t('common:KEY')}</Label>
                  <Input value={payload?.key} disabled />
                </InnerGroup>
              </Col>
            </FormGroup>

            {!isFetching && !translation?.id ? (
              <EmptyState>{t('messages:TRANSLATION_NOT_FOUND')}</EmptyState>
            ) : (
              <>
                <InnerGroup data-testid='english-field'>
                  <Label required>{t('common:ENGLISH')}</Label>
                  <TextField control={control} name='valueEn' placeholder={t('common:ENTER')} required />
                </InnerGroup>
                <InnerGroup data-testid='german-field'>
                  <Label required>{t('common:GERMAN')}</Label>
                  <TextField control={control} name='valueDe' placeholder={t('common:ENTER')} required />
                </InnerGroup>
                <InnerGroup data-testid='french-field'>
                  <Label required>{t('common:FRENCH')}</Label>
                  <TextField control={control} name='valueFr' placeholder={t('common:ENTER')} required />
                </InnerGroup>
                <InnerGroup data-testid='italian-field'>
                  <Label required>{t('common:ITALIAN')}</Label>
                  <TextField control={control} name='valueIt' placeholder={t('common:ENTER')} required />
                </InnerGroup>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={handleClose} disabled={isSubmitting} ghost dataTestId='cancel-button'>
              {t('common:CANCEL')}
            </Button>
            <Button type='submit' isLoading={isSubmitting} disabled={isDisabled} primary dataTestId='submit-button'>
              {t('common:SAVE')}
            </Button>
          </Modal.Footer>
        </form>
      </FormProvider>
    </Modal>
  );
});
